import { skipToken } from "@reduxjs/toolkit/query";
import type { TrendPeriodScores } from "@repo/app-types/entities/attainmentTrend";
import {
  asPlaybookId,
  collectPlaybookCriteria,
  type PlaybookCriterionNode,
} from "@repo/app-types/entities/playbook";
import { PlaybookReportType } from "@repo/app-types/entities/playbookReport";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DateTime } from "luxon";
import { useMemo, type FC, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import {
  usePlaybookReportPeriodsQuery,
  usePlaybookReportQuery,
} from "../../api/entities/performancePlaybook.js";
import {
  useAllMinimalPlaybooksQuery,
  usePlaybookByIdQuery,
} from "../../api/entities/playbook.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { AwardCard, type AwardCardData } from "./monthInReview/awardCard.js";
import { formatCount } from "./monthInReview/deltaPill.js";
import { HighlightMomentCard } from "./monthInReview/highlightMomentCard.js";
import { MetricCard } from "./monthInReview/metricCard.js";
import { ScoreMovementChart } from "./monthInReview/scoreMovementChart.js";

// ============================================================================
// Helpers
// ============================================================================

const cardClassName =
  "rounded-md bg-white px-4 pb-4 pt-3 shadow-sm ring-1 ring-black/5";

function formatPeriodLabel(period: string): string {
  const dt = DateTime.fromFormat(period, "yyyy-MM");
  return dt.isValid ? dt.toFormat("LLLL yyyy") : period;
}

/**
 * Find the teammate with the highest current_score in a set of trend scores.
 */
function findHighestPerformer(
  attainment: Record<TeammateId, TrendPeriodScores>,
): { teammateId: TeammateId; endingScore: number } | null {
  let best: { teammateId: TeammateId; endingScore: number } | null = null;

  for (const [teammateId, scores] of recordEntries(attainment)) {
    if (scores.current_score === null) {
      continue;
    }
    if (best === null || scores.current_score > best.endingScore) {
      best = { teammateId, endingScore: scores.current_score };
    }
  }

  return best;
}

// ============================================================================
// Sub-components
// ============================================================================

const SectionHeader: FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div>
    <h2 className="text-xl font-semibold text-foreground">{title}</h2>
    {subtitle ? (
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    ) : null}
  </div>
);

const CriterionCard: FC<{
  name: string;
  scores: Record<string, TrendPeriodScores>;
  teammates: readonly Teammate[];
  gradingValueType: "yes_no" | "1_5_score";
}> = ({ name, scores, teammates, gradingValueType }) => (
  <div className={cardClassName}>
    <div className="text-sm font-semibold text-foreground">{name}</div>
    <div className="mt-4 border-t border-gray-100 pt-4">
      <ScoreMovementChart
        scores={scores}
        teammates={teammates}
        gradingValueType={gradingValueType}
      />
    </div>
  </div>
);

// ============================================================================
// Main component
// ============================================================================

export const PlaybookMonthInReview: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("playbookId params are required");
  }
  const playbookId = asPlaybookId(playbookIdParam);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedPeriod = searchParams.get("period");

  // ---- Playbook dropdown ----
  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery();

  const playbookOptions: SimpleSelectOption<string>[] = useMemo(
    () => minimalPlaybooks?.map((p) => ({ label: p.name, value: p.id })) ?? [],
    [minimalPlaybooks],
  );

  const onPlaybookChange = (value: string | null) => {
    if (value) {
      navigate(appPaths.playbookReview.build({ playbookId: value }), {
        replace: true,
      });
    }
  };

  // ---- Report periods ----
  const {
    data: reportPeriods,
    isLoading: isPeriodsLoading,
    error: periodsError,
  } = usePlaybookReportPeriodsQuery(
    playbookId
      ? { playbookId, reportType: PlaybookReportType.MonthInReview }
      : skipToken,
  );

  const periodOptions: SimpleSelectOption<string>[] = useMemo(
    () =>
      reportPeriods?.map((p) => ({
        label: formatPeriodLabel(p.period),
        value: p.period,
      })) ?? [],
    [reportPeriods],
  );

  const effectivePeriod = useMemo(() => {
    if (selectedPeriod) {
      return selectedPeriod;
    }
    const firstReportPeriod = reportPeriods?.[0];
    if (firstReportPeriod) {
      return firstReportPeriod.period;
    }
    return null;
  }, [selectedPeriod, reportPeriods]);

  const onPeriodChange = (value: string | null) => {
    if (value) {
      setSearchParams({ period: value });
    }
  };

  // ---- Report data ----
  const {
    currentData: report,
    isFetching: isReportLoading,
    error: reportError,
  } = usePlaybookReportQuery(
    playbookId && effectivePeriod
      ? {
          playbookId,
          reportType: PlaybookReportType.MonthInReview,
          period: effectivePeriod,
        }
      : skipToken,
  );

  // ---- Playbook details for criteria names and outcome definition ----
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  // ---- Teammates for avatars/names ----
  const { data: allTeammates } = useAllTeammatesQuery();

  const teammatesMap = useMemo(() => {
    const map = new Map<string, Teammate>();
    for (const t of allTeammates ?? []) {
      map.set(t.id, t);
    }
    return map;
  }, [allTeammates]);

  const activeTeammates = useMemo(
    () => allTeammates?.filter((t) => !t.deactivated_at) ?? [],
    [allTeammates],
  );

  // ---- Criteria lookup from playbook assessment tree ----
  const criteriaMap = useMemo(() => {
    if (!playbook) return new Map<string, PlaybookCriterionNode>();
    const all = collectPlaybookCriteria(playbook.assessment_tree);
    return new Map(all.map((c) => [c.id, c]));
  }, [playbook]);

  // ---- Computed data from report ----
  const reportData = report?.data;

  const periodLabel = effectivePeriod ? formatPeriodLabel(effectivePeriod) : "";

  const quickMetrics = useMemo(() => {
    if (!reportData) return [];

    const totalInteractions = reportData.customer_interactions.total_count;
    const totalCalls = reportData.calls.total_count;

    return [
      {
        label: "Customer interactions",
        value: formatCount(totalInteractions),
        delta: "—",
        deltaTone: "neutral" as const,
        comparison: "",
      },
      {
        label: "Total calls",
        value: formatCount(totalCalls),
        delta: "—",
        deltaTone: "neutral" as const,
        comparison: "",
      },
    ];
  }, [reportData]);

  // Pivot criteria_attainment from Record<TeammateId, Record<NodeId, Scores>>
  // to Record<NodeId, Record<TeammateId, Scores>> for per-criterion display.
  const criteriaAttainmentByNode = useMemo(() => {
    if (!reportData) return {};
    const result: Record<string, Record<TeammateId, TrendPeriodScores>> = {};
    for (const [teammateId, nodeScores] of recordEntries(
      reportData.criteria_attainment,
    )) {
      for (const [nodeId, scores] of Object.entries(nodeScores)) {
        if (!result[nodeId]) {
          result[nodeId] = {};
        }
        result[nodeId][teammateId] = scores;
      }
    }
    return result;
  }, [reportData]);

  const highImpactCriteria = useMemo(() => {
    if (!reportData) return [];
    return reportData.high_impact_criteria_ids
      .map((id) => {
        const criterion = criteriaMap.get(id);
        const scores = criteriaAttainmentByNode[id];
        if (!criterion || !scores) return null;
        return { criterion, scores };
      })
      .filter(
        (
          item,
        ): item is {
          criterion: PlaybookCriterionNode;
          scores: Record<string, TrendPeriodScores>;
        } => item !== null,
      );
  }, [reportData, criteriaMap, criteriaAttainmentByNode]);

  const awards = useMemo(() => {
    if (!reportData) return [];

    const result: (AwardCardData & { key: string })[] = [];

    // Overall playbook top performer
    const overallBest = findHighestPerformer(reportData.playbook_attainment);
    if (overallBest) {
      const teammate = teammatesMap.get(overallBest.teammateId);
      const displayName = teammate
        ? buildTeammateDisplayNames(teammate).primaryDisplayName
        : overallBest.teammateId;

      result.push({
        key: "playbook-top",
        title: "Top performer",
        recipient: displayName,
        teammate,
        detail: `Playbook adherence ${Math.round(overallBest.endingScore * 100)}%`,
        accent: "gold",
      });
    }

    // Per-criterion top performers
    for (const { criterion, scores } of highImpactCriteria) {
      const best = findHighestPerformer(scores);
      if (!best) continue;

      const teammate = teammatesMap.get(best.teammateId);
      const displayName = teammate
        ? buildTeammateDisplayNames(teammate).primaryDisplayName
        : best.teammateId;

      result.push({
        key: `criteria-${criterion.id}`,
        title: `Best in ${criterion.name}`,
        recipient: displayName,
        teammate,
        detail: formatScoreForAward(
          best.endingScore,
          criterion.grading_value_type,
        ),
        accent: "blue",
      });
    }

    return result;
  }, [reportData, highImpactCriteria, teammatesMap]);

  const highlights = useMemo(() => {
    if (!reportData || !playbookId) return [];

    return reportData.customer_quotes.map((quote, index) => {
      const teammate = teammatesMap.get(quote.teammate_id);
      const teammateName = teammate
        ? buildTeammateDisplayNames(teammate).primaryDisplayName
        : quote.teammate_id;

      // Prefer the grading id (new pattern); fall back to the tuple URL for
      // legacy quotes without one (the redirect wrapper resolves it).
      const gradingUrl = quote.grading_id
        ? appPaths.playbookCallTranscript.build({
            playbookId,
            gradingId: quote.grading_id,
          })
        : // Quotes without a grading id link the legacy (teammate, account)
          // tuple URL, which GradingTupleRedirect resolves to the grading.
          `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(quote.teammate_id)}/accounts/${encodeURIComponent(quote.account_id)}/transcript`;

      return {
        key: `${quote.teammate_id}-${quote.account_id}-${index}`,
        teammate,
        teammateName,
        quote: quote.quote,
        gradingUrl,
      };
    });
  }, [reportData, playbookId, teammatesMap]);

  // ---- Render ----

  let content: ReactNode;
  if (isPeriodsLoading) {
    content = (
      <div className="flex items-center justify-center py-16">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  } else if (periodsError) {
    content = (
      <p className="text-destructive">Failed to load report periods.</p>
    );
  } else if (!reportPeriods || reportPeriods.length === 0) {
    content = (
      <p className="text-muted-foreground">
        No reports are available for this playbook yet.
      </p>
    );
  } else if (isReportLoading) {
    content = (
      <div className="flex items-center justify-center py-16">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  } else if (reportError) {
    content = <p className="text-destructive">Failed to load report data.</p>;
  } else if (reportData) {
    content = (
      <>
        {/* Performance snapshot */}
        {quickMetrics.length > 0 && (
          <section className="space-y-4">
            <SectionHeader
              title="Performance snapshot"
              subtitle={periodLabel}
            />
            <div className="grid gap-4 md:grid-cols-3">
              {quickMetrics.map((metric) => (
                <MetricCard key={metric.label} {...metric} />
              ))}
            </div>
          </section>
        )}

        {/* High impact criteria */}
        {highImpactCriteria.length > 0 && (
          <section className="space-y-4">
            <SectionHeader title="High impact criteria" />
            <div className="grid gap-4 md:grid-cols-2">
              {highImpactCriteria.map(({ criterion, scores }) => (
                <CriterionCard
                  key={criterion.id}
                  name={criterion.name}
                  scores={scores}
                  teammates={activeTeammates}
                  gradingValueType={criterion.grading_value_type}
                />
              ))}
            </div>
          </section>
        )}

        {/* Awards */}
        {awards.length > 0 && (
          <section className="space-y-4">
            <SectionHeader
              title="Awards and recognition"
              subtitle="Top performers this month."
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {awards.map(({ key, ...award }) => (
                <AwardCard
                  key={key}
                  className={cardClassName}
                  header={`${periodLabel} awards`}
                  {...award}
                />
              ))}
            </div>
          </section>
        )}

        {/* Highlights */}
        {highlights.length > 0 && (
          <section className="space-y-4">
            <SectionHeader
              title="Highlights"
              subtitle="The best moments from this month's calls."
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {highlights.map((h) => (
                <HighlightMomentCard
                  key={h.key}
                  teammate={h.teammate}
                  teammateName={h.teammateName}
                  quote={h.quote}
                  gradingUrl={h.gradingUrl}
                />
              ))}
            </div>
          </section>
        )}
      </>
    );
  } else {
    content = null;
  }

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Wide}
        title="Month in review"
        actions={
          <>
            {periodOptions.length > 0 && (
              <SimpleSelect
                label="Period"
                isLabelHidden
                value={effectivePeriod}
                options={periodOptions}
                onChange={onPeriodChange}
              />
            )}
            {playbookOptions.length > 0 && (
              <SimpleSelect
                label="Playbook"
                isLabelHidden
                value={playbookId ?? null}
                options={playbookOptions}
                onChange={onPlaybookChange}
              />
            )}
          </>
        }
      />
      <PageBody width={PageWidth.Wide}>{content}</PageBody>
    </PageLayout>
  );
};

// ============================================================================
// Utilities
// ============================================================================

function formatScoreForAward(
  score: number,
  gradingValueType: "yes_no" | "1_5_score",
): string {
  if (gradingValueType === "yes_no") {
    return `Score ${Math.round(score * 100)}%`;
  }
  return `Score ${score.toFixed(1)} / 5`;
}
